<template>
  <div v-if="campagne">
    <div class="entete border-entete">
      <div class="is-paddingless section">
        <div class="level">
          <div class="level-left">
            <div class="columns is-gapless">
              <div class="column max-width-60vw">
                <h1 class="title is-size-3">
                  {{ getTitle }}</h1>
                <breadcrumb :breadcrumb="breadcrumb"/>
              </div>
            </div>
          </div>
          <div class="level-right">
            <div class="buttons">
              <a class="button"
                @click="$router.go(-1)">
                {{ $t('general.button.cancel') }}
              </a>
              <a class="button is-addOrEdit" @click="save">
                {{ $t('general.button.sauvegarder') }}
              </a>
              <piece-jointe-button
                class="mgr-2"
                :perm="constantes.CAMPAGNE.maj">
              </piece-jointe-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <error-msg-component class="mgt-20px"
    :messages="messages"
    :messagesChampsVide="messagesChampsVide"
    :champsVide="champsVide"
    :error="error"
    :title="'general.problemOccurred'"
    :withEmplyFields="true"
    @close="error = $event"/>

    <div class="columns mgt-20px">
      <div class="column is-narrow">
        <app-logo :noCropper="true"></app-logo>
      </div>
      <div class="column">
        <div class="card">
          <header class="header-card">
            <p>{{$t('editionCampagne.informationsHeader')}}</p>
          </header>
          <div class="card-content">
            <div class="columns is-multiline">
              <b-field :label="$t('editionCampagne.informations.libelle')"
                class="column is-6 obligatoire">
                <b-input v-model="campagne.libelle"></b-input>
              </b-field>
              <b-field :label="$t('editionCampagne.informations.libelleInterne')"
                class="column is-6">
                <b-input v-model="campagne.libelleInterne"></b-input>
              </b-field>
              <div class="column is-6 obligatoire">
                <parametre-autocomplete
                  :label="$t('editionCampagne.informations.marque')"
                  :target="constantes.PARAMETRES_KEYS.marque"
                  v-model="campagne.marqueId"
                  field="libelle"
                  selector="id"
                  expanded
                />
              </div>
              <b-field :label="$t('editionCampagne.informations.tags')" class="column is-6">
                <query-tags-tag-input
                  :tooltipLabel="$t('editionCampagne.tagInfo')"
                  v-model="campagne.tags"
                  field="tag"
                  :moduleTag="constantes.moduleTagCampagne">
                </query-tags-tag-input>
              </b-field>
              <b-field :label="$t('editionCampagne.informations.dateDebut')"
                class="column is-3">
                <b-datepicker show-week-number
                        nearby-selectable-month-days
                        v-model="campagne.dateDebut"
                        icon="calendar-today"
                        :editable="true">
                </b-datepicker>
              </b-field>
              <b-field :label="$t('editionCampagne.informations.dateFin')"
                class="column is-3">
                <b-datepicker show-week-number
                        nearby-selectable-month-days
                        v-model="campagne.dateFin"
                        icon="calendar-today"
                        :editable="true">
                </b-datepicker>
              </b-field>
              <b-field :label="$t('editionCampagne.informations.newsLetter')"
                class="column is-2">
                <qd-checkbox v-model="campagne.newsLetter"
                            :value="campagne.newsLetter">
                </qd-checkbox>
              </b-field>
              <b-field :label="$t('editionCampagne.informations.visibleParLaMarque')"
                class="column is-2">
                <qd-checkbox v-model="campagne.visibleParLaMarque"
                            :value="campagne.visibleParLaMarque">
                </qd-checkbox>
              </b-field>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <header class="header-card">
        <p>
          {{ $t('piecesJointes.attachments')}}
        </p>
      </header>
      <div class="card-content">
        <pieces-jointes :checkPermissions="true"/>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import breadcrumb from '@/components/general/breadcrumb';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import { get, create, update } from '@/api/campagne/index';
import messageErreur from '@/services/general/messageErreur';
import messageSuccess from '@/services/general/messageSucces';
import genererCampagne from '@/services/bodac/campagne/genererCampagne';
import translate from '@/services/bodac/campagne/translatorCampagne';
import validation from '@/services/validation/validation';
import Logo from '@/components/general/Logo';
import piecesJointes from '@/components/general/piecesJointes';
import pieceJointeButton from '@/components/general/button/pieceJointeButton';
import { CAMPAGNE } from '@/configuration/permissionsBodacConf';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import queryTagsTagInput from '@/components/general/tagInput/queryTagsTagInput';
import { CAMPAGNE as moduleTagCampagne } from '@/configuration/parametrage/tagConf';
import gestionDateBase from '@/services/general/gestionDateBase';
import formatDateIso from '@/services/general/formatIsoDate';
import { SET_CAMPAGNE, SET_DEMANDE_IMPRESSION } from '@/store/modules/logistique/demandeImpression-mutation-type';
import {
  ROUTE_ALLOWED_WITHOUT_CLEAN_STORE_FOR_CAMPAGNE,
} from '@/configuration/logistique/demandeImpression/demandeImpressionConf';
import parametreAutocomplete from '@/components/general/autocomplete/parametreAutocomplete';

const Societe = createNamespacedHelpers('societe');
const Attachments = createNamespacedHelpers('attachments');
const DemandeImpression = createNamespacedHelpers('demandeImpression');

export default {
  name: 'EditionCampagne',
  components: {
    breadcrumb,
    errorMsgComponent,
    piecesJointes,
    appLogo: Logo,
    pieceJointeButton,
    queryTagsTagInput,
    parametreAutocomplete,
  },
  data() {
    return {
      campagne: null,
      error: false,
      messages: [],
      messagesChampsVide: [],
      champsVide: [],
      libelle: null,
      routeFrom: null,
      constantes: {
        moduleTagCampagne,
        CAMPAGNE,
        PARAMETRES_KEYS,
      },
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    ...DemandeImpression.mapGetters({
      demandeImpressionStore: 'demandeImpression',
    }),
    breadcrumb() {
      return [
        { title: this.$t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('listeCampagnes.title'), route: { name: 'l.bodac.campagne' }, isActive: false },
        {
          title: this.$route.params.id ? this.$t('editionCampagne.titleModification', { libelle: this.libelle }) : this.$t('editionCampagne.titleCreation'),
          route: { name: '' },
          isActive: true,
        },
      ];
    },
    getTitle() {
      return this.$route.params.id
        ? this.$t('editionCampagne.titleModification', { libelle: this.libelle })
        : this.$t('editionCampagne.titleCreation');
    },
  },
  methods: {
    ...Attachments.mapActions({
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
      insertAttachments: 'insert',
      updateAttachments: 'update',
    }),
    ...DemandeImpression.mapMutations({
      setCampagne: SET_CAMPAGNE,
      setDemandeImpression: SET_DEMANDE_IMPRESSION,
    }),
    async save() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        let campagne = {};
        this.messages = [];
        this.messagesChampsVide = [];
        this.champsVide = [];
        this.error = false;
        const campagneCopie = cloneDeep(this.campagne);
        campagneCopie.dateDebut = formatDateIso(campagneCopie.dateDebut);
        campagneCopie.dateFin = formatDateIso(campagneCopie.dateFin);
        if (this.$route.params.id) {
          campagne = (await update(campagneCopie)).data.result;
          await this.updateAttachments(campagne.id);
          messageSuccess(this.$t('editionCampagne.messages.successModification'));
        } else {
          campagne = (await create(campagneCopie)).data.result;
          await this.insertAttachments(campagne.id);
          messageSuccess(this.$t('editionCampagne.messages.successCreation'));
        }
        const routeTo = this.routeFrom || { name: 'c.bodac.campagne', params: { id: campagne.id } };
        if (this.routeFrom) this.setCampagne(campagne);
        await this.$router.push(routeTo);
      } catch (e) {
        if (e.response.data.error) {
          validation(this.$i18n, e.response, this.champsVide,
            this.messages, this.messagesChampsVide, translate);
          this.error = true;
        } else messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
    initializeCampagne(campagne) {
      campagne.dateDebut = gestionDateBase(campagne.dateDebut);
      campagne.dateFin = gestionDateBase(campagne.dateFin);
      this.campagne = campagne;
    },
  },
  beforeRouteEnter(to, from, next) {
    const routesNameSave = [
      'c.logistique.demande_impression',
      'm.logistique.demande_impression-add',
      'm.logistique.demande_impression-edit',
    ];
    const { name, params } = from;
    next((vm) => {
      if (routesNameSave.includes(from.name)) vm.routeFrom = { name, params };
    });
  },
  beforeRouteLeave(to, from, next) {
    if (!ROUTE_ALLOWED_WITHOUT_CLEAN_STORE_FOR_CAMPAGNE.includes(to.name)) {
      this.setDemandeImpression(null);
      this.setCampagne(null);
    }
    next();
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.setAttachmentContext({ module: 'Campagne' });
      if (this.$route.params.id) {
        const campagne = (await get(this.$route.params.id)).data.result;
        this.initializeCampagne(campagne);
        await this.setAttachmentElementId({ id: this.campagne.id });
        this.libelle = cloneDeep(this.campagne.libelle);
        this.setInformationPage(this.$t('pageGeneral.modification', { item: this.campagne.libelle }));
      } else {
        const campagneGenerer = genererCampagne(this.societeActiveId);
        if (this.demandeImpressionStore?.client?.tiersMarques?.length) {
          campagneGenerer.marqueId = this.demandeImpressionStore.client.tiersMarques[0]?.marque?.id;
        }
        this.campagne = campagneGenerer;
        this.setInformationPage(this.$t('pageGescom.campagne.creation'));
      }
      loadingComponent.close();
    } catch (error) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw error;
    }
  },
};
</script>

<style>

</style>
